import { defineStore } from "pinia";
import { useAsyncOperation } from "../Composables/useAsyncOperation";
import { ref, nextTick, computed } from "vue";
import { useRightDrawerStore } from "./RightDrawer";
import EventBus from "../Utilities/EventBus";
import { useRolesAndPermissions } from "./RolesAndPermissions";
import { useAppState } from "./AppState";
import { MAIN_MODULES, USER_SETTING_OPTIONS } from "../Constants/menus";
import { useSettings } from "../Components/Settings/Store/Settings";
import Utilities from "../Helpers/utilities";
import { useUserStore } from "./User";
import { useConversation } from "./Conversation";
import { useWorkspace } from "./Workspaces";
import { ROUTE_NAMES } from "../Constants/routes";
import { useUnlinkedPanelState } from "./useUnlinkedPanelState";
import {
    MEET_YOUR_KAAMFU_AI,
    MEET_YOUR_KAAMFU_AI_WORKER,
    INVITE_YOUR_TEAM,
    DESIGNATE_WORKSPACE_ADMINS,
    CONFIGURE_WORKDAY_SETTINGS,
    ENABLE_ENTERPRISE_SUPPORT,
    DEFINE_MANDATES_FOR_WORKSTATION_CONNECT,
    BUILD_PROJECTS_IN_THE_WORK_PANEL,
    MANAGE_WORKSPACE_REPORTS,
    CREATE_YOUR_PERSONAL_PROFILE,
    CREATE_AN_ORGANIZATION_PROFILE,
    INSTALL_KAAMFU_WORKSTATION_CONNECT,
    ACCEPT_INVITATION,
    KNOW_YOUR_KAAMFU,
    profileDummyStep,
    orgOwnerDummyStep,
    getDummyOrganizationInvitationStep,
} from "Admin/js/Constants/menus";
import { useWorkspaceTree } from "./WorkspaceTree";

export const useAlignStore = defineStore("align", () => {
    const { manageAsyncOperation } = useAsyncOperation();
    const rightDrawerStore = useRightDrawerStore();
    const rolesAndPermissionStore = useRolesAndPermissions();
    const appStateStore = useAppState();
    const settingsStore = useSettings();
    const { toggleChatPopover } = Utilities();
    const userStore = useUserStore();
    const conversationStore = useConversation();
    const workspacesStore = useWorkspace();
    const workspaceTree = useWorkspaceTree();
    const unlinkedPanelState = useUnlinkedPanelState();

    const alignSteps = ref([]);
    const selectedStep = ref(null);
    const supportAdmin = ref(null);
    const isLoading = ref(false);
    const isTogglingESA = ref(false);

    const CUSTOM_ACTION_STEPS = {
        [MEET_YOUR_KAAMFU_AI]: () => {
            rightDrawerStore.closeDrawer();
            toggleChatPopover(true);
            appStateStore.setIsAgent(true);
            conversationStore.createDMWithKaamfuAgent(userStore.agent);
        },
        [MEET_YOUR_KAAMFU_AI_WORKER]: () => {
            rightDrawerStore.closeDrawer();
            toggleChatPopover(true);
            appStateStore.setIsAgent(true);
            conversationStore.createDMWithKaamfuAgent(userStore.agent);
        },
    };

    const completedSteps = computed(() => {
        let steps = alignSteps.value ?? [];

        if (userStore.isOwner) {
            steps = [orgOwnerDummyStep, ...steps];
        } else if (appStateStore.invitation_accepted?.inviter) {
            steps = [
                getDummyOrganizationInvitationStep(
                    userStore.profile?.organization?.name,
                    appStateStore.invitation_accepted?.inviter?.first_name,
                ),
                ...steps,
            ];
        }
        steps = [profileDummyStep, ...steps];
        return steps;
    });

    const actions = computed(() => ({
        [DESIGNATE_WORKSPACE_ADMINS]: {
            icon: "Admin",
            action: async () => {
                const moduleUserSettings = MAIN_MODULES.find(
                    (mod) => mod.name === ROUTE_NAMES.SETTINGS,
                );

                appStateStore.setRoleObject(rolesAndPermissionStore.getAdmins);
                appStateStore.setSelectedModule(moduleUserSettings);
                settingsStore.switchSettingTab(
                    USER_SETTING_OPTIONS.organizations.name,
                );
                appStateStore.goToSettings();

                nextTick(() => {
                    setTimeout(() => {
                        rightDrawerStore.openAdministration();
                        EventBus.$emit("pre-fill-organization-role-users");
                    }, 50);
                });
            },
        },
        [CONFIGURE_WORKDAY_SETTINGS]: {
            icon: "Workday",
            action: async () => {
                const moduleUserSettings = MAIN_MODULES.find(
                    (mod) => mod.name === ROUTE_NAMES.SETTINGS,
                );
                appStateStore.setSelectedModule(moduleUserSettings);
                settingsStore.switchSettingTab(
                    USER_SETTING_OPTIONS.workday.name,
                );
                appStateStore.goToSettings();
                nextTick(() => {
                    setTimeout(() => {
                        toggleChatPopover(false);
                        rightDrawerStore.openOfficeHours();
                    }, 50);
                });
            },
        },
        [INVITE_YOUR_TEAM]: {
            icon: "Invite",
            custom: true,
            component: "InviteTeamBody",
        },
        [ENABLE_ENTERPRISE_SUPPORT]: {
            icon: "Esa",
            loading: isTogglingESA,
            action: async () => {
                if (isTogglingESA.value) {
                    return;
                }

                isTogglingESA.value = true;

                try {
                    const wasEnabled = !!supportAdmin.value;

                    if (supportAdmin.value) {
                        await userStore.disableAdminSupport();
                        supportAdmin.value = null;
                    } else {
                        const data = await userStore.enableAdminSupport();
                        if (data?.id) {
                            try {
                                unlinkedPanelState.restrictRecoveryMode();
                                await conversationStore.createDMWithProfile(
                                    data.id,
                                );
                            } catch (error) {
                                unlinkedPanelState.unrestrictRecoveryMode();
                            }
                        }
                        supportAdmin.value = data;
                    }

                    await fetchAlignSteps(false);
                    findAndSetSupportAdminStep();
                    const actionType = wasEnabled ? "disabled" : "enabled";
                    appStateStore.setShowSupportAdminDialog(true, actionType);
                } finally {
                    isTogglingESA.value = false;
                }
            },
        },
        [DEFINE_MANDATES_FOR_WORKSTATION_CONNECT]: {
            icon: "Mandate",
            action: () => {
                appStateStore.setSelectedModule(
                    MAIN_MODULES.find(
                        (mod) => mod.name === ROUTE_NAMES.DIRECTORY,
                    ),
                );
                appStateStore.goToDirectory();
            },
        },
        [BUILD_PROJECTS_IN_THE_WORK_PANEL]: {
            icon: "Project",
            action: () => {
                const workModule = appStateStore.getModuleWork;
                workspacesStore.last_clicked_entity = {
                    type: null,
                    entity: null,
                };
                workspaceTree.resetActiveState();
                workspacesStore.openSpacesPanel();
                appStateStore.setSelectedModule(workModule);
                appStateStore.goToWork();
                // Wait for panel to be fully rendered before locating button
                nextTick(() => {
                    setTimeout(() => {
                        EventBus.$emit("locate-new-space-button");
                    }, 500);
                });
            },
        },
        [MANAGE_WORKSPACE_REPORTS]: {
            icon: "Report",
            action: () => {
                const reportsModule = MAIN_MODULES.find(
                    (mod) => mod.name === ROUTE_NAMES.REPORTS,
                );
                appStateStore.setSelectedModule(reportsModule);
                appStateStore.goToReports();
            },
        },
        [CREATE_YOUR_PERSONAL_PROFILE]: {
            custom: true,
            component: "PersonalProfileBody",
            hideStatus: true,
        },
        [CREATE_AN_ORGANIZATION_PROFILE]: {
            icon: "Invite",
            custom: true,
            component: "OrganizationProfileBody",
            hideStatus: true,
        },
        [INSTALL_KAAMFU_WORKSTATION_CONNECT]: {
            custom: true,
            component: "InstallKaamfuWorkstationConnectBody",
            hideStatus: true,
        },
        [ACCEPT_INVITATION]: {
            custom: true,
            component: "AcceptInvitationBody",
            hideStatus: true,
        },
        [KNOW_YOUR_KAAMFU]: {
            custom: true,
            icon: "Report",
            component: "KnowYourKaamfuBody",
            hideStatus: true,
        },
    }));

    function fetchAlignSteps(showLoading = true) {
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.get("/align-steps");
                const previousSelectedStepId = selectedStep.value?.id;
                const steps = data?.data ?? [];
                alignSteps.value = steps.map((step) => {
                    if (CUSTOM_ACTION_STEPS[step.type]) {
                        return {
                            ...step,
                            custom: true,
                            action: CUSTOM_ACTION_STEPS[step.type],
                            hidePanelData: true,
                        };
                    }
                    return step;
                });
                if (previousSelectedStepId) {
                    const isDummyStep = [98, 99, 100, 103].includes(
                        previousSelectedStepId,
                    );
                    const stepsToSearch = isDummyStep
                        ? completedSteps.value
                        : alignSteps.value;
                    const updatedStep = stepsToSearch.find(
                        (step) => step.id === previousSelectedStepId,
                    );
                    if (updatedStep) {
                        selectedStep.value = updatedStep;
                    }
                }
            },
            () => {},
            (val) => {
                if (showLoading) {
                    isLoading.value = val;
                }
            },
        );
    }

    function createOrUpdateAlignStep(payload) {
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.post("/align-steps", payload);
                const updatedStep = data?.data;
                alignSteps.value = alignSteps.value.map((step) =>
                    step.id === updatedStep?.id ? updatedStep : step,
                );
                return updatedStep;
            },
            () => {},
            (val) => {
                isLoading.value = val;
            },
        );
    }
    function markAlignStepAsCompleted(align_step_id) {
        const newSteps = alignSteps.value.map((step) => {
            return step.id === align_step_id
                ? { ...step, is_completed: true }
                : step;
        });
        alignSteps.value = newSteps;
    }

    function setSelectedStep(step) {
        selectedStep.value = step;
    }
    async function handleHelpNow() {
        if (supportAdmin.value) {
            await conversationStore.createDMWithProfile(supportAdmin.value.id);
        } else {
            toggleChatPopover(false);
            appStateStore.invite_drawer.is_team_opened = false;
            const supportAdminStep = alignSteps.value.find(
                (step) => step.type === ENABLE_ENTERPRISE_SUPPORT,
            );
            setSelectedStep(supportAdminStep);
            rightDrawerStore.openAlign();
        }
    }

    function findAndSetSupportAdminStep() {
        const supportAdminStep = alignSteps.value.find(
            (step) => step.type === ENABLE_ENTERPRISE_SUPPORT,
        );
        setSelectedStep(supportAdminStep);
    }
    return {
        isLoading,
        fetchAlignSteps,
        createOrUpdateAlignStep,
        alignSteps,
        selectedStep,
        setSelectedStep,
        actions,
        supportAdmin,
        handleHelpNow,
        findAndSetSupportAdminStep,
        completedSteps,
        isTogglingESA,
        markAlignStepAsCompleted,
    };
});
